/**
 * Don Price / dj-giardco: Keep Platter's cart in sync after checkout/Redo.
 *
 * External apps can mutate Shopify's cart without updating Alpine. Observe
 * completed cart requests (no fetch/XHR patches), then compare a fresh GET
 * with the native store. Only a mismatch uses Platter's existing get(), which
 * preserves its bundle components, history, and reactive pricing behavior.
 * No quantities, cookies, discounts, checkout hooks, or entry math are changed.
 */
(() => {
  'use strict';
  if (window.djCartReconcile) return;

  let timer;
  let running = false;
  let revision = 0;
  let attempts = 0;
  const signature = cart => JSON.stringify([
    cart?.currency?.iso_code ?? cart?.currency,
    cart?.total_price,
    cart?.original_total_price,
    cart?.cart_level_discount_applications,
    (cart?.items ?? []).map(item => [
      item.key, item.quantity, item.original_line_price, item.final_line_price,
      item.line_level_discount_allocations,
    ]),
  ]);
  const busy = store => !store || store.loading || store.isChanging ||
    store.isSubscriptionChanging || Object.keys(store.debounce_updates ?? {}).length > 0;

  function schedule(delay = 250) {
    clearTimeout(timer);
    timer = setTimeout(reconcile, delay);
  }
  function request() {
    revision++;
    attempts = 0;
    schedule();
  }
  async function reconcile() {
    if (document.visibilityState === 'hidden') return;
    if (running) return; // The in-flight check schedules a newer revision below.
    const store = window._stores?.cart;
    if (!window._cart?.get || busy(store)) {
      // Bounded wait for Alpine initialization or a native quantity operation.
      if (++attempts < 40) schedule(250);
      return;
    }
    running = true;
    const started = revision;
    const before = signature(store.state);
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart refresh unavailable');
      const fresh = await response.json();
      if (!Array.isArray(fresh.items)) throw new Error('Invalid cart refresh');

      // Don Price / dj-giardco: Never reconcile a response over a newer edit.
      if (started !== revision || busy(store) || before !== signature(store.state)) {
        schedule();
        return;
      }
      if (before !== signature(fresh)) {
        await window._cart.get();
        // get() may have joined an existing native request. Verify once more.
        if (++attempts < 3) schedule();
      }
    } catch (error) {
      // Preserve the customer's current cart on network failure. No empty fallback.
      if (++attempts < 3) schedule(1000);
      else console.warn('[Cart sync] Could not refresh cart; retry on next interaction.');
    } finally {
      clearTimeout(timeout);
      running = false;
      if (started !== revision) schedule();
    }
  }

  // Exposed only for an explicit retry and regression tests; no cart data stored.
  window.djCartReconcile = { request };
  window.addEventListener('pageshow', request); // Includes back/forward cache restores.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') request();
  });
  document.addEventListener('alpine:initialized', request);

  // Don Price / dj-giardco: Redo and other app writes do not emit a common cart
  // event. Resource timing sees successful/failed completions without intercepting
  // their requests. Our GET /cart.js is deliberately not a matching mutation.
  if (typeof PerformanceObserver !== 'undefined') {
    const observer = new PerformanceObserver(list => {
      if (list.getEntries().some(entry => {
        try {
          const url = new URL(entry.name, location.href);
          return url.origin === location.origin &&
            /\/cart\/(add|change|update|clear)(\.js)?\/?$/.test(url.pathname);
        } catch { return false; }
      })) request();
    });
    observer.observe({ type: 'resource', buffered: true });
  }
  request();
})();
